import React, {useState, useEffect, useRef} from 'react';
import NoteService from '../../../services/NoteService';
import {MdOutlineExpandMore, MdOutlineExpandLess, MdSkipPrevious, MdPause, MdPlayArrow, MdSkipNext, MdQueueMusic} from "react-icons/md";
import parse from 'html-react-parser';
import useIsMobile from "../../../utils/useIsMobile";
import Spinner2 from "../../../UI/Spinner/Spinner2";
import Dictaphone1 from "../../../utils/Assist";
import {useParams} from "react-router-dom";


const AccordionT = ({ title, parameter, onExpandClick, id, number, videoNames }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [contentWithImages, setContentWithImages] = useState([]);
    const [audioFiles, setAudioFiles] = useState([]);
    const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPlayingAll, setIsPlayingAll] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const audioRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [isFirstPlayed, setIsFirstPlayed] = useState(false);
    const accordionRef = useRef(null);
    const [videos, setVideos] = useState([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

    const isMobile = useIsMobile();

    const { studentId } = useParams();

    const toggleAccordion = async () => {
        if (!isOpen && !dataFetched) {
            setLoading(true);
            try {
                const response = await NoteService.getDescription2(parameter, number, studentId);
                const { text, images, videos: videoUrls, audio } = response;
                setVideos(videoUrls || []);
                const paragraphs = text ? text.split('\n') : [];
                const contentWithImages = paragraphs.map((paragraph, index) => ({
                    paragraph: processText(paragraph, images, videoUrls),
                    paragraphNumber: index + 1,
                }));
                setContentWithImages(contentWithImages);
                setAudioFiles(Array.isArray(audio) ? audio : []);
                setDataFetched(true);
            } catch (error) {
                console.error('Error fetching accordion data:', error);
            } finally {
                setLoading(false);
            }
        }

        // Open or close the accordion without redundant calls
        setIsOpen(!isOpen);

        if (!isOpen && accordionRef.current) {
            accordionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    };

    const processText = (text, imageUrls, videoUrls) => {
        const stegRegex = /steg\s(1[0-9]|20|[1-9])/gi;
        const fallRegex = /fall\s(1[0-9]|20|[1-9])/gi;
        const automatRegex = /-AUTOMAT-/gi;
        const manuellRegex = /-MANUELL-/gi;
        const numberDotRegex = /(?:^|\s)(1[0-9]|20|[1-9])\./g;
        const redRegex = /\*\*RED\*\*([^\*]+)\*\*/g;
        const boldRegex = /\*\*BOLD\*\*([^\*]+)\*\*/g;
        const greenRegex = /\*\*GREEN\*\*([^\*]+)\*\*/g;
        const infoIconPlaceholder = /__INFO_ICON__/g;  // Use this as a placeholder
        const names = /\[cat([A-H])([1-9]|[1-9][0-9]|1[0-9]{2}|200)_(1[0-9]|20|[1-9])_vid\]/g; // Use this as a placeholder
        const breakReg = /\*\*BREAK\*\*/g;  // Updated regex to match **BREAK** without capturing additional text

        // Start processing the text and store it in processedText
        let processedText = text
            .replace(stegRegex, match => `<strong class="highlighted">${match}</strong>`)
            .replace(fallRegex, match => `<strong class="highlighted">${match}</strong>`)
            .replace(automatRegex, match => `<strong class="highlighted2">${match}</strong>`)
            .replace(manuellRegex, match => `<strong class="highlighted2">${match}</strong>`)
            .replace(numberDotRegex, match => `<strong class="highlighted">${match}</strong>`)
            .replace(redRegex, (match, redText) => `<strong class="text-red">${redText}</strong>`)
            .replace(boldRegex, (match, boldText) => `<strong>${boldText}</strong>`)
            .replace(greenRegex, (match, greenText) => `<strong class="text-green">${greenText}</strong>`)
            .replace(breakReg, (match, breakText) => `<br><p style="padding-bottom: 10px;"></p>`)
            .replace(infoIconPlaceholder, match => `<strong class="info-icon">I</strong>`)
            // Process video names
            .replace(names, (match, catGroup, number, index) => {
                const videoIndex = videoUrls.findIndex((url) => url.includes(match.replace(/[\[\]]/g, '').replace('_vid', '')));
                if (videoIndex !== -1) {
                    return `<div style="margin: 10px 0; text-align: center;">
                        <video src="${videoUrls[videoIndex]}" controls style="max-width: 80%; border-radius: 8px; height: auto;"></video>
                    </div>`;
                }
                return match; // Return original marker if no video found
            })
            // Process image markers
            .replace(/\[cat([A-H])([1-9]|[1-9][0-9]|1[0-9]{2}|200)_(1[0-9]|20|[1-9])\]/g, (match, catGroup, number, index) => {
                const imageIndex = parseInt(index, 10) - 1;
                const imageUrl = imageUrls[imageIndex];
                return imageUrl ? `<span style="display: flex; justify-content: center; padding: 8px 0;">
                    <img className="image" src="${imageUrl}" alt="Step image" style="max-width:80%; height:50%; border-radius: 8px;" />
                </span>` : match;
            });

        return processedText;
    };

    const playNextAudioVoice = () => {
        if (audioFiles.length > 0 && audioRef.current) {
            audioRef.current.src = audioFiles[currentAudioIndex];
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch((error) => {
                    console.error("Error playing audio:", error);
                });
        }
    };
    const nextAudioVoice = () => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        const newIndex = (currentAudioIndex + 1) % audioFiles.length;
        setCurrentAudioIndex(newIndex);
        if (audioRef.current && audioFiles.length > 0) {
            audioRef.current.src = audioFiles[newIndex];
        }
        if (!isFirstPlayed) {
            setIsFirstPlayed(true);
        }
        playNextAudioVoice();
    }
    const nextAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        const newIndex = (currentAudioIndex + 1) % audioFiles.length;
        setCurrentAudioIndex(newIndex);
        audioRef.current.src = audioFiles[newIndex];
        setTimeout(playAudio, 100); // Small delay to ensure source update is reflected
    };
    const prevAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();}
        const newIndex = (currentAudioIndex - 1) % audioFiles.length;
        setCurrentAudioIndex(newIndex);// Update the audio source with the new index
        audioRef.current.src = audioFiles[newIndex];// Then play the audio after the source is set
        setTimeout(playAudio, 100); // Small delay to ensure source update is reflected
    };
    const playAudio = () => {
        if (audioFiles.length > 0 && audioRef.current) {
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch((error) => {
                    console.error("Error playing audio:", error);
                });}
    };
    const playAudioButton = () => {
        if (audioFiles.length > 0 && audioRef.current) {
            audioRef.current.src = audioFiles[currentAudioIndex];
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch((error) => {
                    console.error("Error playing audio:", error);
                });}
    };
    const playAudioVoice = (param2) => {
        if (!param2 || typeof param2 !== 'string') {
            return;}
        const category = param2.slice(0, 4); // Extract category
        const filteredFiles = audioFiles.filter(file => file.includes(category));
        if (filteredFiles.length > 0 && audioRef.current) {
            audioRef.current.src = filteredFiles[0];
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch((error) => {
                    console.error("Error playing audio:", error);
                });
        } else {
            console.log(`No audio files found for param2: ${param2}`);
        }
    };
    const pauseAudio = () => { console.log('pause activated pressed')
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };
    const playAllAudio = () => {
        setIsPlayingAll(true);
        setCurrentAudioIndex(0);
    };
    const stopAllAudio = () => {
        pauseAudio();
        setIsPlayingAll(false);
        setCurrentAudioIndex(0);
    };
    useEffect(() => {
        if (isPlayingAll) {
            if (currentAudioIndex < audioFiles.length && audioRef.current) {
                audioRef.current.src = audioFiles[currentAudioIndex];
                audioRef.current.play().catch((error) => console.error("Error playing audio:", error));
                setIsPlaying(true);
            } else {
                stopAllAudio();
            }
        }
    }, [currentAudioIndex, isPlayingAll]);
    const handleAudioEnded = () => {
        if (isPlayingAll) {
            setCurrentAudioIndex((prevIndex) => prevIndex + 1);
        } else {
            setIsPlaying(false);
        }};
    const handleVoiceCommand = (command, param2) => {
        switch (command) {
            case 'nästa steg':
                nextAudioVoice();
                break;
            case 'första steg':
                if (param2) {
                    playAudioVoice(param2); // Pass param2 to playAudioVoice
                }
                setCurrentAudioIndex(1); // Ensure index is set to the first item
                setIsFirstPlayed(true); // Mark as played to prevent replays
                break;
            case 'avsluta läsning':
                stopAllAudio();
                break;
            case 'läs alla steg':
                playAllAudio();
                break;
            default:console.log('Unrecognized command:', command);
        }};
    return (
        <><div ref={accordionRef}>
            {isMobile ? (
                <div className="accordion-m center">
                    <div
                        className="accordion-header center2"
                        style={{ backgroundColor: '#f1ebff', padding: '10px', marginBottom: '-5px', borderRadius: '5px' }}
                        onClick={toggleAccordion}>
                        <span>{title}</span>
                        {isOpen ? <MdOutlineExpandLess className="custom-icon" /> : <MdOutlineExpandMore className="custom-icon" />}
                    </div>
                    {isOpen && (
                        <div style={{ backgroundColor: '#f1ebff', padding: '9px', borderRadius: '5px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Spinner2 />
                                </div>
                            ) : (
                                <>
                                    {contentWithImages.map(({ paragraph, paragraphNumber }) => (
                                        <div key={paragraphNumber}>
                                            <span style={{ textAlign: 'left' }}>{parse(paragraph)}</span>
                                        </div>
                                    ))}
                                    <span className='center2 jcm jcc'>
                                    </span>
                                    {audioFiles.length > 0 && (
                                        <div className="audio-controls" style={{ textAlign: 'center', padding:'10px', margin: '5px',backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                            <Dictaphone1
                                                onCommand={handleVoiceCommand}
                                                isAccordionOpen={isOpen}
                                                id={id}
                                                audioFiles={audioFiles}
                                                audioRef={audioRef}
                                                parameter={parameter}
                                            />
                                            <audio
                                                ref={audioRef}
                                                onEnded={handleAudioEnded}
                                            />
                                            <p style={{fontSize: '14pt', color: '#54319f', padding: '9px'}} > Styra instruktioner med knappar  </p>
                                            <button className='ghostbuttonXS' onClick={prevAudio}>
                                                <MdSkipPrevious /> Förra
                                            </button>
                                            {isPlaying ? (
                                                <button className="ghostbuttonXS" onClick={pauseAudio}>
                                                    <MdPause /> Paus
                                                </button>
                                            ) : (
                                                <button className="ghostbuttonXS" onClick={playAudioButton}>
                                                    <MdPlayArrow /> Spela
                                                </button>
                                            )}
                                            <button className='ghostbuttonXS' onClick={nextAudio}>
                                                <MdSkipNext /> Nästa
                                            </button>

                                            <button className='ghostbuttonXS' onClick={playAllAudio}>
                                                <MdQueueMusic /> Läs alla
                                            </button>
                                        </div>)}</>)}</div>)}</div>
            ) : (
                <div className="accordion">
                    <div
                        className="accordion-header center2"
                        style={{ backgroundColor: '#f1ebff', padding: '10px', marginBottom: '-5px', borderRadius: '5px' }}
                        onClick={toggleAccordion}>
                        <span>{title}</span>
                        {isOpen ? <MdOutlineExpandLess className="custom-icon" /> : <MdOutlineExpandMore className="custom-icon" />}
                    </div>
                    {isOpen && (
                        <div style={{ backgroundColor: '#f1ebff', padding: '9px', borderRadius: '5px' }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Spinner2 />
                                </div>
                            ) : (
                                <>{contentWithImages.map(({ paragraph, paragraphNumber }) => (
                                    <div key={paragraphNumber}>
                                        <section style={{ textAlign: 'left' }}>{parse(paragraph)}</section>
                                    </div>
                                ))}
                                    {audioFiles.length > 0 && (
                                        <><div className="audio-controls" style={{ textAlign: 'center',  margin: '5px', marginBottom:'15px', backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                            <Dictaphone1
                                                onCommand={handleVoiceCommand}
                                                isAccordionOpen={isOpen}
                                                id={id}
                                                audioFiles={audioFiles}
                                                audioRef={audioRef}
                                                parameter={parameter} // Passing parameter from Accordion to Dictaphone1
                                            />
                                        </div>
                                            <div className="audio-controls" style={{ textAlign: 'center', padding:'10px', margin: '5px',backgroundColor: '#f8f4ff', borderRadius: '5px' }}>
                                                <audio
                                                    ref={audioRef}
                                                    onEnded={handleAudioEnded}/>
                                                <p style={{fontSize: '14pt', color: '#54319f', padding: '9px'}} > Styra med knappar  </p>
                                                <button className='ghostbuttonXS' onClick={prevAudio}>
                                                    <MdSkipPrevious /> Förra
                                                </button>
                                                {isPlaying ? (
                                                    <button className="ghostbuttonXS" onClick={pauseAudio}>
                                                        <MdPause /> Paus
                                                    </button>
                                                ) : (
                                                    <button className="ghostbuttonXS" onClick={playAudioButton}>
                                                        <MdPlayArrow /> Spela
                                                    </button>
                                                )}
                                                <button className='ghostbuttonXS' onClick={nextAudio}>
                                                    <MdSkipNext /> Nästa
                                                </button>
                                                <button className='ghostbuttonXS' onClick={playAllAudio}>
                                                    <MdQueueMusic /> Läs alla
                                                </button>
                                            </div></>)}</>)}</div>)}</div>)}</div>
        </>
    );
};
export default AccordionT;
